import React, { useEffect, useMemo, useReducer, useState } from 'react';
import PropTypes from 'prop-types';
import { useDebounce } from 'use-debounce';
import { Dialog } from '@mui/material';
import IngredientStructuredDataEditHomePage from './ingredient_structured_data_edit_home_page';
import IngredientStructuredDataEditSearchIngredientPage from './ingredient_structured_data_edit_search_ingredient_page';
import {
  ingredientWithTokenAdded,
  preparationString,
  rewritePreparation,
} from '../../services/ingredients';
import { scaleNutrition } from '../../services/nutrition';
import {
  getFoodDataItemOperation,
  searchIngredientDataOperation,
} from '../../operations/recipes_operations';
import { LinkedIngredientSource, StructuredIngredientSource } from '../../API';
import { deriveNutritionFromIngredientsOperation } from '../../operations/meal_operations';

const ingredientReducer = (ingredientState, action) => {
  switch (action.type) {
    case 'FOOD_DATA_ITEM_CHOSEN': {
      const { foodDataItem } = action;
      let ingredientNutrition = {};
      const unitOfMeasure =
        ingredientState.ingredient.structuredIngredient?.unitOfMeasure;
      const quantity =
        ingredientState.ingredient.structuredIngredient?.quantity;
      if (unitOfMeasure && !Number.isNaN(parseFloat(quantity))) {
        const selectedMeasure = (foodDataItem.supportedMeasures || []).find(
          (measure) => measure.unitOfMeasure === unitOfMeasure,
        );
        if (selectedMeasure) {
          ingredientNutrition = scaleNutrition(
            foodDataItem.nutritionPer100g,
            (selectedMeasure.gramsPerUnit * parseFloat(quantity)) / 100,
          );
        }
      }

      return {
        ...ingredientState,
        ingredient: {
          ...ingredientState.ingredient,
          structuredIngredient: {
            ...(ingredientState.ingredient.structuredIngredient || {}),
            linkedIngredient: {
              linkedIngredientName: foodDataItem.foodName,
              status: true,
              linkedIngredientSource: LinkedIngredientSource.FOOD_DATA,
              foodDataID: foodDataItem.foodDataID,
            },
          },
        },
        ingredientNutrition,
        ingredientNutritionPer100g: foodDataItem.nutritionPer100g || {},
        supportedMeasures: foodDataItem.supportedMeasures,
      };
    }

    case 'STRUCTURED_INGREDIENT_NAME_CHANGED': {
      const { newStructuredIngredientName } = action;
      return {
        ...ingredientState,
        ingredient: {
          ...ingredientState.ingredient,
          structuredIngredient: {
            ...(ingredientState.ingredient.structuredIngredient || {}),
            name: newStructuredIngredientName,
          },
        },
      };
    }

    case 'UNIT_OF_MEASURE_CHANGED': {
      const { newUnitOfMeasure } = action;
      const selectedMeasure = ingredientState.supportedMeasures.find(
        (measure) => measure.unitOfMeasure === newUnitOfMeasure,
      );
      if (!selectedMeasure) {
        return ingredientState;
      }
      const ingredientNutrition = scaleNutrition(
        ingredientState.ingredientNutritionPer100g,
        (selectedMeasure.gramsPerUnit *
          parseFloat(
            ingredientState.ingredient.structuredIngredient.quantity,
          )) /
          100,
      );
      return {
        ...ingredientState,
        ingredient: {
          ...ingredientState.ingredient,
          structuredIngredient: {
            ...(ingredientState.ingredient.structuredIngredient || {}),
            unitOfMeasure: newUnitOfMeasure,
          },
        },
        ingredientNutrition,
      };
    }

    case 'QUANTITY_CHANGED': {
      const { newQuantity } = action;
      let { ingredientNutrition } = ingredientState;
      const quantityFloat = parseFloat(newQuantity);
      if (!Number.isNaN(quantityFloat)) {
        const selectedMeasure = ingredientState.supportedMeasures.find(
          (measure) =>
            measure.unitOfMeasure ===
            ingredientState.ingredient.structuredIngredient.unitOfMeasure,
        );
        if (selectedMeasure) {
          ingredientNutrition = scaleNutrition(
            ingredientState.ingredientNutritionPer100g,
            (selectedMeasure.gramsPerUnit * quantityFloat) / 100,
          );
        }
      }
      return {
        ...ingredientState,
        ingredient: {
          ...ingredientState.ingredient,
          structuredIngredient: {
            ...(ingredientState.ingredient.structuredIngredient || {}),
            quantity: newQuantity,
          },
        },
        ingredientNutrition,
      };
    }

    case 'INGREDIENT_NUTRITION_OVERRIDDEN': {
      const { ingredientNutritionOverride } = action;
      return {
        ...ingredientState,
        ingredientNutrition: ingredientNutritionOverride,
      };
    }

    case 'PREPARATION_CHANGED': {
      const { newPreparation } = action;
      return {
        ...ingredientState,
        ingredient: rewritePreparation(
          ingredientState.ingredient,
          newPreparation,
        ),
      };
    }

    case 'INGREDIENT_LINE_REWRITTEN': {
      let newIngredient = {
        ...ingredientState.ingredient,
        tokens: [],
        tokFullText: '',
      };
      if (ingredientState.ingredient.structuredIngredient?.quantity) {
        newIngredient = ingredientWithTokenAdded(
          newIngredient,
          'QTY',
          ingredientState.ingredient.structuredIngredient.quantity.toString(),
        );
      }
      if (ingredientState.ingredient.structuredIngredient?.unitOfMeasure) {
        newIngredient = ingredientWithTokenAdded(
          newIngredient,
          'UNITS',
          ingredientState.ingredient.structuredIngredient?.unitOfMeasure,
        );
      }
      if (ingredientState.ingredient.structuredIngredient?.name) {
        newIngredient = ingredientWithTokenAdded(
          newIngredient,
          'ING',
          ingredientState.ingredient.structuredIngredient?.name,
        );
      }
      if (preparationString(ingredientState.ingredient)) {
        newIngredient = ingredientWithTokenAdded(
          newIngredient,
          'PREP',
          preparationString(ingredientState.ingredient),
        );
      }
      newIngredient.fullText = newIngredient.tokFullText;
      return {
        ...ingredientState,
        ingredient: newIngredient,
      };
    }

    case 'SUPPORTED_MEASURES_AVAILABLE': {
      const { supportedMeasures } = action;
      return {
        ...ingredientState,
        supportedMeasures,
      };
    }

    default: {
      return ingredientState;
    }
  }
};

const IngredientStructuredDataEditPopup = ({
  initialIngredient,
  initialIngredientNutrition,
  anchorEl,
  onUpdate,
  onDismiss,
}) => {
  const [page, setPage] = useState('homePage');

  const [
    { ingredient, ingredientNutrition, supportedMeasures },
    ingredientDispatch,
  ] = useReducer(ingredientReducer, {
    ingredient: initialIngredient,
    ingredientNutrition: initialIngredientNutrition,
    ingredientNutritionPer100g: initialIngredientNutrition,
    supportedMeasures:
      null /* initialIngredient.structuredIngredient?.unitOfMeasure
      ? [
          {
            unitOfMeasure:
              initialIngredient.structuredIngredient?.unitOfMeasure,
          },
        ]
      : null, */,
  });

  const memoizedSupportedMeasures = useMemo(
    () => supportedMeasures,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(supportedMeasures)],
  );

  const { linkedIngredientName, linkedIngredientSource, foodDataID } =
    ingredient.structuredIngredient?.linkedIngredient || {};

  useEffect(() => {
    if (linkedIngredientName && memoizedSupportedMeasures === null) {
      if (
        linkedIngredientSource === LinkedIngredientSource.FOOD_DATA &&
        !!foodDataID
      ) {
        getFoodDataItemOperation(foodDataID).then((foodDataItem) => {
          if (foodDataItem) {
            ingredientDispatch({
              type: 'FOOD_DATA_ITEM_CHOSEN',
              foodDataItem,
            });
          }
        });
      } else {
        searchIngredientDataOperation(
          {
            searchString: linkedIngredientName,
          },
          0,
        ).then((result) => {
          if (result.data && result.data.length > 0) {
            ingredientDispatch({
              type: 'SUPPORTED_MEASURES_AVAILABLE',
              supportedMeasures:
                result.data[0].supportedMeasures ||
                (result.data[0].supportedUnits || []).map((unit) => ({
                  unitOfMeasure: unit,
                })),
            });
          }
        });
      }
    }
  }, [
    foodDataID,
    linkedIngredientName,
    linkedIngredientSource,
    memoizedSupportedMeasures,
  ]);

  console.log('Ingredient follows');
  console.log(JSON.stringify(ingredient));
  console.log(JSON.stringify(ingredientNutrition));

  const selectedMeasure = (memoizedSupportedMeasures || []).find(
    (measure) =>
      measure.unitOfMeasure === ingredient.structuredIngredient?.unitOfMeasure,
  );
  const quantityFloat = parseFloat(ingredient.structuredIngredient?.quantity);

  const errors = {};
  if (!linkedIngredientName || linkedIngredientName === '') {
    errors.linkedIngredientName = 'Ingredient is required';
  }
  if (
    !ingredient.structuredIngredient?.name ||
    ingredient.structuredIngredient?.name === ''
  ) {
    errors.structuredIngredientName = 'Structured ingredient name is required';
  }
  if (!selectedMeasure) {
    errors.unitOfMeasure = 'Unit of measure is required';
  }
  if (Number.isNaN(quantityFloat)) {
    errors.quantity = 'Quantity is required';
  }

  const onClickSearchIngredient = () => {
    setPage('searchIngredient');
  };

  const onClickUpdate = () => {
    onUpdate(
      {
        ...ingredient,
        structuredIngredientSource: StructuredIngredientSource.MANUAL,
      },
      ingredientNutrition,
    );
  };

  const onClickRewriteIngredientLine = () => {
    ingredientDispatch({
      type: 'INGREDIENT_LINE_REWRITTEN',
    });
  };

  const onChooseFoodDataItem = (foodDataItem) => {
    ingredientDispatch({
      type: 'FOOD_DATA_ITEM_CHOSEN',
      foodDataItem,
    });
    setPage('homePage');
  };

  const onChangeStructuredIngredientName = (newStructuredIngredientName) =>
    ingredientDispatch({
      type: 'STRUCTURED_INGREDIENT_NAME_CHANGED',
      newStructuredIngredientName,
    });

  const onChangeUnitOfMeasure = (newUnitOfMeasure) =>
    ingredientDispatch({
      type: 'UNIT_OF_MEASURE_CHANGED',
      newUnitOfMeasure,
    });

  const onChangeQuantity = (newQuantity) =>
    ingredientDispatch({
      type: 'QUANTITY_CHANGED',
      newQuantity,
    });

  const onChangePreparation = (newPreparation) =>
    ingredientDispatch({
      type: 'PREPARATION_CHANGED',
      newPreparation,
    });

  const [debouncedQuantity] = useDebounce(quantityFloat, 500);
  useEffect(() => {
    if (linkedIngredientSource !== LinkedIngredientSource.FOOD_DATA) {
      if (selectedMeasure && !Number.isNaN(debouncedQuantity)) {
        deriveNutritionFromIngredientsOperation(
          [
            {
              ...ingredient,
              structuredIngredient: {
                ...ingredient.structuredIngredient,
                unitOfMeasure: selectedMeasure.unitOfMeasure,
                quantity: parseFloat(debouncedQuantity),
              },
              // Force reanalysis of the ingredient
              structuredIngredientSource: StructuredIngredientSource.AUTOMATIC,
            },
          ],
          1,
          '',
          '',
          null,
          null,
        ).then((deriveNutritionResult) => {
          if (
            deriveNutritionResult.ingredientNutrition &&
            deriveNutritionResult.ingredientNutrition[0]
          ) {
            const ingredientNutritionOverride =
              deriveNutritionResult.ingredientNutrition[0].nutrition;
            ingredientDispatch({
              type: 'INGREDIENT_NUTRITION_OVERRIDDEN',
              ingredientNutritionOverride,
            });
          }
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    // eslint-disable-next-line react-hooks/exhaustive-deps
    JSON.stringify(selectedMeasure),
    debouncedQuantity,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    JSON.stringify(ingredient),
    linkedIngredientSource,
  ]);

  return (
    <Dialog open maxWidth="sm" fullWidth anchorEl={anchorEl}>
      {page === 'homePage' && (
        <IngredientStructuredDataEditHomePage
          fullText={ingredient.fullText}
          linkedIngredientName={linkedIngredientName}
          structuredIngredientName={ingredient.structuredIngredient?.name}
          unitOfMeasure={ingredient.structuredIngredient?.unitOfMeasure}
          supportedMeasures={memoizedSupportedMeasures || []}
          quantity={ingredient.structuredIngredient?.quantity}
          preparation={preparationString(ingredient)}
          nutrition={ingredientNutrition}
          errors={errors}
          onDismiss={onDismiss}
          onClickUpdate={onClickUpdate}
          onClickRewriteIngredientLine={onClickRewriteIngredientLine}
          onClickSearchIngredient={onClickSearchIngredient}
          onChangeStructuredIngredientName={onChangeStructuredIngredientName}
          onChangeUnitOfMeasure={onChangeUnitOfMeasure}
          onChangeQuantity={onChangeQuantity}
          onChangePreparation={onChangePreparation}
        />
      )}
      {page === 'searchIngredient' && (
        <IngredientStructuredDataEditSearchIngredientPage
          initialSearchText={
            ingredient.structuredIngredient?.linkedIngredient
              ?.linkedIngredientName
          }
          onBack={() => setPage('homePage')}
          onChooseFoodDataItem={onChooseFoodDataItem}
        />
      )}
    </Dialog>
  );
};

IngredientStructuredDataEditPopup.propTypes = {
  initialIngredient: PropTypes.shape({
    fullText: PropTypes.string.isRequired,
    tokFullText: PropTypes.string,
    tokens: PropTypes.arrayOf(
      PropTypes.shape({
        fromChar: PropTypes.number,
        toChar: PropTypes.number,
        type: PropTypes.string,
      }),
    ),
    structuredIngredient: PropTypes.shape({
      unitOfMeasure: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  initialIngredientNutrition: PropTypes.object.isRequired,
  anchorEl: PropTypes.element.isRequired,
  onUpdate: PropTypes.func.isRequired,
  onDismiss: PropTypes.func.isRequired,
};

export default IngredientStructuredDataEditPopup;
